/* eslint-disable @typescript-eslint/no-magic-numbers */
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition, ScrollStrategy } from '@angular/cdk/overlay';
import { ViewportRuler } from '@angular/cdk/scrolling';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  ViewChild,
} from '@angular/core';
import { NgControl, ValidationErrors } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Observable, Subject, of } from 'rxjs';
import { distinctUntilChanged, map, take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { v4 as uuid } from 'uuid';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import {
  MatTimeValuePart,
  MatTimeValueProcessor,
} from '@nimbus-library/features/shared/components/material/mat-time/mat-time-value.processor';
import { convertFromTeamsClientCoordinates, isContainsInSVG } from '@nimbus-library/features/shared/components/material/utils';
import { MatTimeAnimations } from '@nimbus-library/features/shared/components/material/mat-time/mat-time.animations';
import { IMicrosoftTeamsContextAccessor } from '@nimbus-library/features/microsoft-teams/contract/i-microsoft-teams-context.accessor';
import { ClientType } from '@nimbus-library/features/microsoft-teams/models/client-type';
import { DateUtils } from '@nimbus-library/features/shared/utils/date.utils';
import { MatTimeService } from '../mat-time.service';

class MatTimePickerBase {
  public readonly stateChanges: Subject<void> = new Subject<void>();
  public _ariaDescribedby: string;
  public errorState: boolean;

  public constructor(
    public elementRef: ElementRef,
    public ngControl: NgControl,
  ) {}

  public open(): void {}
  public propagateChange: <T>(value: T) => void = () => void 0;
  public propagateTouched: () => object = () => ({});

  public registerOnChange(fn: <T>(value: T) => void): void {
    this.propagateChange = fn;
  }

  public registerOnTouched(fn: () => object): void {
    this.propagateTouched = fn;
  }

  public setDescribedByIds(ids: string[]): void {
    this._ariaDescribedby = ids.join(' ');
  }

  public onContainerClick(): void {
    this.focus();
    this.open();
  }

  public focus(options?: FocusOptions): void {
    this.elementRef.nativeElement.focus(options);
  }
}

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nimbus-mat-time-picker',
  templateUrl: 'mat-time-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'mat-time-picker',
    '[attr.id]': 'id',
    '[attr.tabindex]': 'tabIndex',
    '[class.mat-time-picker-empty]': 'empty',
  },
  animations: [MatTimeAnimations.transformPanelWrap, MatTimeAnimations.transformPanel],
  providers: [{ provide: MatFormFieldControl, useExisting: MatTimePickerComponent }],
})
export class MatTimePickerComponent extends MatTimePickerBase implements OnDestroy, OnInit, DoCheck {
  @ViewChild('panel')
  public panel: ElementRef;
  @ViewChild(CdkConnectedOverlay, { static: true })
  public overlayDir: CdkConnectedOverlay;
  @ViewChild('trigger', { static: true })
  public trigger: ElementRef;
  @ViewChild('daysInput')
  public daysInput: ElementRef<HTMLInputElement>;
  @ViewChild('hoursInput')
  public hoursInput: ElementRef<HTMLInputElement>;
  @ViewChild('minutesInput')
  public minutesInput: ElementRef<HTMLInputElement>;
  @ViewChild('secondsInput')
  public secondsInput: ElementRef<HTMLInputElement>;

  @Input()
  public disabled: boolean;
  @Input()
  public required: boolean;
  @Input()
  public id: string = `mat-time-picker-${uuid()}`;
  @Input()
  public tabIndex: number = 0;
  @Input()
  public errors: ValidationErrors;
  @Input()
  public errorStateMatcher: ErrorStateMatcher = new ErrorStateMatcher();

  @Output()
  public readonly touched = new EventEmitter<void>();

  private format: string = 'HH:mm:ss';
  private innerValue: Date;
  private innerPlaceholder: string;
  private scrollTop = 0;
  private triggerRect: ClientRect;

  public panelDoneAnimatingStream = new Subject<string>();
  public isMinutesEnabled: boolean = true;
  public isHoursEnabled: boolean = true;
  public isSecondsEnabled: boolean = true;
  public isDaysEnabled: boolean = true;
  public isScalable: boolean = false;
  public isPanelOpen: boolean = false;
  public valuePart: typeof MatTimeValuePart = MatTimeValuePart;

  public positions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'top',
    },
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'bottom',
    },
  ];

  public valueProcessor: MatTimeValueProcessor;
  public scrollStrategy: ScrollStrategy;
  public offsetY = 0;

  public TranslationKeys = TranslationKeys;

  public teamsClient$: Observable<boolean> = this.microsoftTeamsContextAccessor
    ? this.microsoftTeamsContextAccessor.getClientInfo$().pipe(
        take(1),
        map((x) => x !== ClientType.Browser),
      )
    : of(false);

  @Input()
  public get value(): Date {
    return this.innerValue;
  }
  public set value(value: Date) {
    if (value !== this.innerValue) {
      this.innerValue = value;
      this.valueProcessor.parseValue(this.innerValue);
      this.propagateChange(value);
    }
  }

  @Input()
  public get placeholder(): string {
    return this.innerPlaceholder;
  }
  public set placeholder(value: string) {
    this.innerPlaceholder = value;
    this.stateChanges.next();
  }

  @Input()
  public set timeFormat(value: string) {
    this.isDaysEnabled = value?.toLowerCase().includes('dd');
    this.isHoursEnabled = value?.toLowerCase().includes('hh');
    this.isMinutesEnabled = value?.toLowerCase().includes('mm');
    this.isSecondsEnabled = value?.toLowerCase().includes('ss');

    if (value !== this.format) {
      this.format = value;
      this.stateChanges.next();
    }
  }
  public get timeFormat(): string {
    return this.format;
  }

  public get empty(): boolean {
    return !this.innerValue;
  }

  public get triggerValue(): string {
    if (this.empty) {
      return this.placeholder ?? '';
    }

    return DateUtils.formatDate(this.innerValue, this.format);
  }

  public get shouldLabelFloat(): boolean {
    return this.isPanelOpen || !this.empty;
  }

  public constructor(
    private readonly microsoftTeamsContextAccessor: IMicrosoftTeamsContextAccessor,
    private readonly viewportRuler: ViewportRuler,
    private readonly matTimeService: MatTimeService,
    private readonly elementReference: ElementRef<HTMLElement>,
    private readonly changeDetectorRef: ChangeDetectorRef,
    @Self() @Optional() public ngControl: NgControl,
  ) {
    super(elementReference, ngControl);

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    this.valueProcessor = new MatTimeValueProcessor(this.matTimeService);
  }

  public ngOnInit(): void {
    this.panelDoneAnimatingStream.pipe(distinctUntilChanged(), untilDestroyed(this)).subscribe(() => {
      if (this.isPanelOpen) {
        this.scrollTop = 0;
      } else {
        this.overlayDir.offsetX = 0;
        this.changeDetectorRef.markForCheck();
      }
    });

    this.viewportRuler
      .change()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        if (this.isPanelOpen) {
          this.triggerRect = this.trigger.nativeElement.getBoundingClientRect();
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  public ngDoCheck(): void {
    if (this.ngControl) {
      this.updateErrorState();
    }
  }

  public ngOnDestroy(): void {
    this.stateChanges.complete();
  }

  public updateErrorState(): void {
    this.ngControl.control.setErrors(this.errors);
    const errorState = this.errorStateMatcher.isErrorState(this.ngControl.control, null);
    if (errorState !== this.errorState) {
      this.errorState = errorState;
      this.stateChanges.next();
    }
  }

  public onHoursValueChanged(value: string): void {
    if (value && value.length === 2 && this.isMinutesEnabled && this.minutesInput) {
      this.minutesInput.nativeElement.focus();
    }
  }

  public onMinutesValueChanged(value: string): void {
    if (value && value.length === 2 && this.isSecondsEnabled && this.secondsInput) {
      this.secondsInput.nativeElement.focus();
    }
  }

  public togglePanel(): void {
    if (this.isPanelOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  public writeValue(value: Date): void {
    if (value !== this.innerValue) {
      this.innerValue = value;
      this.valueProcessor.parseValue(this.innerValue);
    }
    this.changeDetectorRef.markForCheck();
  }

  public open(): void {
    if (this.disabled || this.isPanelOpen) {
      return;
    }

    this.triggerRect = this.trigger.nativeElement.getBoundingClientRect();
    this.valueProcessor.parseValue(this.innerValue);

    this.isPanelOpen = true;
    this.calculateOverlayPosition();

    setTimeout(() => {
      const firstInput = this.daysInput || this.hoursInput || this.minutesInput || this.secondsInput;
      if (firstInput) {
        firstInput.nativeElement.focus();
      }
    }, 100);

    this.changeDetectorRef.markForCheck();
  }

  public close(): void {
    if (this.isPanelOpen) {
      this.isPanelOpen = false;
      this.changeDetectorRef.markForCheck();
      this.propagateTouched();
      this.touched.emit();
    }
  }

  public onCancelClick(): void {
    this.close();
  }

  public onSaveClick(): void {
    this.value = this.valueProcessor.setSelectedTimeValue(this.innerValue, this.isDaysEnabled);
    this.close();
  }

  public onAttached(): void {
    this.overlayDir.positionChange.pipe(take(1), untilDestroyed(this)).subscribe(() => {
      this.changeDetectorRef.detectChanges();
      this.calculateOverlayOffsetX();
      this.panel.nativeElement.scrollTop = this.scrollTop;
    });
  }

  private calculateOverlayPosition(): void {
    this.isScalable = isContainsInSVG(this.elementReference.nativeElement);
    const globalScale: number = this.isScalable ? +getComputedStyle(document.body).getPropertyValue('--global-scale') : 1;

    const panelHeight: number = parseInt(getComputedStyle(document.body).getPropertyValue('--input-time-picker-panel-max-height') || '115');

    const paddingTop = 29;

    this.teamsClient$.pipe(untilDestroyed(this)).subscribe((isTeamsClient) => {
      if (isTeamsClient) {
        if (this.isScalable) {
          const currentScaledPositionY = (this.overlayDir.origin as CdkOverlayOrigin).elementRef.nativeElement.getBoundingClientRect().y;
          const currentPositionY = convertFromTeamsClientCoordinates(currentScaledPositionY);
          this.offsetY =
            -1 * (currentScaledPositionY - currentPositionY - paddingTop * globalScale - (panelHeight * globalScale - panelHeight) / 2);
        } else {
          this.offsetY = paddingTop * globalScale + (panelHeight * globalScale - panelHeight) / 2;
        }
      } else {
        this.offsetY = paddingTop * globalScale + (panelHeight * globalScale - panelHeight) / 2;
      }
      this.scrollTop = 0;
      this.checkOverlayWithinViewport(0);
    });
  }

  private checkOverlayWithinViewport(maxScroll: number): void {
    const panelHeight: number = parseInt(getComputedStyle(document.body).getPropertyValue('--input-time-picker-panel-max-height') || '115');
    const viewportSize = this.viewportRuler.getViewportSize();

    const topSpaceAvailable = this.triggerRect.top;
    const bottomSpaceAvailable = viewportSize.height - this.triggerRect.bottom;

    const panelHeightTop = Math.abs(this.offsetY);

    const panelHeightBottom = panelHeight - panelHeightTop - this.triggerRect.height;

    if (panelHeightBottom > bottomSpaceAvailable) {
      this.adjustPanelUp(panelHeightBottom, bottomSpaceAvailable);
    } else if (panelHeightTop > topSpaceAvailable) {
      this.adjustPanelDown(panelHeightTop, topSpaceAvailable, maxScroll);
    }
  }

  private adjustPanelUp(panelHeightBottom: number, bottomSpaceAvailable: number): void {
    const distanceBelowViewport = Math.round(panelHeightBottom - bottomSpaceAvailable);

    this.scrollTop -= distanceBelowViewport;
    this.offsetY -= distanceBelowViewport;

    if (this.scrollTop <= 0) {
      this.scrollTop = 0;
      this.offsetY = 0;
    }
  }

  private adjustPanelDown(panelHeightTop: number, topSpaceAvailable: number, maxScroll: number): void {
    const distanceAboveViewport = Math.round(panelHeightTop - topSpaceAvailable);
    this.scrollTop += distanceAboveViewport;
    this.offsetY += distanceAboveViewport;

    if (this.scrollTop >= maxScroll) {
      this.scrollTop = maxScroll;
      this.offsetY = 0;
    }
  }

  private calculateOverlayOffsetX(): void {
    const overlayRect = this.overlayDir.overlayRef.overlayElement.getBoundingClientRect();
    const viewportSize = this.viewportRuler.getViewportSize();

    this.isScalable = isContainsInSVG(this.elementReference.nativeElement);
    const globalScale: number = this.isScalable ? +getComputedStyle(document.body).getPropertyValue('--global-scale') : 1;

    let xPadding: number = parseInt(getComputedStyle(document.body).getPropertyValue('--input-field-flex-padding') || '12');

    const fieldWidth: number = parseInt(getComputedStyle(document.body).getPropertyValue('--input-field-width') || '300');
    xPadding = xPadding * globalScale;

    const paddingWidth = xPadding * 2;
    let offsetX: number;

    offsetX = xPadding;

    offsetX *= -1;

    // Determine how much the select overflows on each side.

    let leftOverflow = 0 - (overlayRect.left + offsetX);
    let rightOverflow = overlayRect.right + offsetX - viewportSize.width + paddingWidth;

    this.teamsClient$.pipe(untilDestroyed(this)).subscribe((isTeamsClient) => {
      if (isTeamsClient) {
        leftOverflow = 0 - (convertFromTeamsClientCoordinates(overlayRect.left) + offsetX);
        rightOverflow = convertFromTeamsClientCoordinates(overlayRect.right) + offsetX - viewportSize.width + paddingWidth;
      }
      if (leftOverflow > 0) {
        offsetX += leftOverflow;
      } else if (rightOverflow > 0) {
        offsetX -= rightOverflow;
      }
      if (isTeamsClient) {
        if (this.isScalable) {
          const currentScaledPositionX = (this.overlayDir.origin as CdkOverlayOrigin).elementRef.nativeElement.getBoundingClientRect().x;
          const currentPositionX = convertFromTeamsClientCoordinates(currentScaledPositionX);
          this.overlayDir.offsetX =
            -1 * (currentScaledPositionX - currentPositionX - offsetX - (fieldWidth * globalScale - fieldWidth) / 2);
        } else {
          this.overlayDir.offsetX = offsetX + (fieldWidth * globalScale - fieldWidth) / 2;
        }
      } else {
        this.overlayDir.offsetX = offsetX + (fieldWidth * globalScale - fieldWidth) / 2;
      }

      this.overlayDir.overlayRef.updatePosition();
    });
  }
}
